import React, { useState } from 'react';
import axios from 'axios';
import { Play, AlertCircle, CheckCircle, Clock, Database, Copy, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const QueryEditor = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);

  const exampleQueries = [
    {
      label: 'Count all users',
      query: 'SELECT COUNT(*) as total FROM users;'
    },
    {
      label: 'Recent reservations',
      query: `SELECT r.id, u.name as user_name, b.name as board_name, ts.start_time
FROM reservations r
JOIN users u ON r.user_id = u.id
JOIN time_slots ts ON r.time_slot_id = ts.id
JOIN boards b ON ts.board_id = b.id
ORDER BY ts.start_time DESC
LIMIT 10;`
    },
    {
      label: 'Gym board counts',
      query: `SELECT g.name as gym_name, COUNT(b.id) as board_count
FROM gyms g
LEFT JOIN boards b ON b.gym_id = g.id
GROUP BY g.id, g.name
ORDER BY board_count DESC;`
    },
    {
      label: 'Active boards by type',
      query: `SELECT type, COUNT(*) as count
FROM boards
WHERE status = 'active'
GROUP BY type
ORDER BY count DESC;`
    }
  ];

  const executeQuery = async () => {
    if (!query.trim()) {
      setError('Please enter a query');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResults(null);

      const response = await axios.post(
        `${API_URL}/api/admin/database/query`,
        { query },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setResults(response.data);

      // Add to history
      setQueryHistory(prev => [
        {
          query,
          timestamp: new Date().toISOString(),
          rowCount: response.data.rowCount,
          success: true
        },
        ...prev.slice(0, 9) // Keep last 10
      ]);
    } catch (err) {
      console.error('Error executing query:', err);
      const errorMessage = err.response?.data?.details || err.response?.data?.error || 'Failed to execute query';
      setError(errorMessage);

      // Add failed query to history
      setQueryHistory(prev => [
        {
          query,
          timestamp: new Date().toISOString(),
          error: errorMessage,
          success: false
        },
        ...prev.slice(0, 9)
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Cmd/Ctrl + Enter to execute
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      executeQuery();
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const downloadCSV = () => {
    if (!results || !results.rows.length) return;

    const headers = results.fields.join(',');
    const rows = results.rows.map(row =>
      results.fields.map(field => {
        const value = row[field];
        if (value === null) return '';
        if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    );

    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query-results-${Date.now()}.csv`;
    a.click();
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (typeof value === 'object') {
      return <span className="text-xs font-mono">{JSON.stringify(value)}</span>;
    }
    if (typeof value === 'string' && value.length > 100) {
      return (
        <span title={value} className="cursor-help">
          {value.substring(0, 100)}...
        </span>
      );
    }
    return String(value);
  };

  return (
    <div className="space-y-4">
      {/* Security Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">SQL Query Editor - Use with Caution</h3>
            <p className="mt-1 text-sm text-yellow-700">
              This tool allows direct database queries. All queries are logged and restricted to SELECT, UPDATE, and DELETE on accessible tables only.
              Sensitive fields are blocked. Press <kbd className="px-1.5 py-0.5 bg-yellow-100 border border-yellow-300 rounded text-xs">Cmd+Enter</kbd> to execute.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Query Editor */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Database className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Query Editor</h3>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => copyToClipboard(query)}
                  disabled={!query}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded disabled:opacity-50"
                  title="Copy query"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <button
                  onClick={executeQuery}
                  disabled={loading || !query.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run Query
                    </>
                  )}
                </button>
              </div>
            </div>

            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter your SQL query here... (Cmd+Enter to execute)"
              className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
            />

            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <div>Allowed: SELECT, UPDATE, DELETE • Blocked: DDL, sensitive fields</div>
              <div>{query.length} characters</div>
            </div>
          </div>

          {/* Results */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-800">Query Error</h4>
                  <p className="mt-1 text-sm text-red-700 font-mono">{error}</p>
                </div>
              </div>
            </div>
          )}

          {results && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span className="text-sm font-medium">Query executed successfully</span>
                  </div>
                  <div className="flex items-center text-gray-500 text-sm">
                    <Clock className="h-4 w-4 mr-1" />
                    {results.executionTime}ms
                  </div>
                  <div className="text-sm text-gray-500">
                    {results.rowCount} {results.rowCount === 1 ? 'row' : 'rows'}
                  </div>
                </div>
                {results.rows.length > 0 && (
                  <button
                    onClick={downloadCSV}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </button>
                )}
              </div>

              {results.rows.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {results.fields.map((field) => (
                          <th
                            key={field}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {field}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="hover:bg-gray-50">
                          {results.fields.map((field) => (
                            <td key={field} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatValue(row[field])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  No rows returned
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Example Queries */}
          <div className="bg-white shadow rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Example Queries</h4>
            <div className="space-y-2">
              {exampleQueries.map((example, index) => (
                <button
                  key={index}
                  onClick={() => setQuery(example.query)}
                  className="w-full text-left px-3 py-2 text-xs bg-gray-50 hover:bg-blue-50 hover:text-blue-600 rounded border border-gray-200 hover:border-blue-300 transition-colors"
                >
                  {example.label}
                </button>
              ))}
            </div>
          </div>

          {/* Query History */}
          {queryHistory.length > 0 && (
            <div className="bg-white shadow rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Recent Queries</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {queryHistory.map((item, index) => (
                  <div
                    key={index}
                    className="text-xs border border-gray-200 rounded p-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setQuery(item.query)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center">
                        {item.success ? (
                          <CheckCircle className="h-3 w-3 text-green-600 mr-1" />
                        ) : (
                          <AlertCircle className="h-3 w-3 text-red-600 mr-1" />
                        )}
                        <span className="text-gray-500">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      {item.success && (
                        <span className="text-gray-500">{item.rowCount} rows</span>
                      )}
                    </div>
                    <div className="font-mono text-gray-700 truncate" title={item.query}>
                      {item.query}
                    </div>
                    {item.error && (
                      <div className="text-red-600 mt-1 truncate" title={item.error}>
                        {item.error}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QueryEditor;
